import {
  BaseUniDriver,
  UniDriver,
} from '@wix/wix-ui-test-utils/dist/types/unidriver';
import { baseUniDriverFactory } from '../utils/test-utils/unidriver';
import { dataAttr, dataHooks } from './Tag.constants';
import { RemoveKey } from './Tag.types';
import { TextSize, TextWeight } from '../Text';
import { textUniDriverFactory } from '../Text/Text.uni.driver';

export interface TagUniDriver extends BaseUniDriver {
  isTiny: () => Promise<boolean>;
  isSmall: () => Promise<boolean>;
  isMedium: () => Promise<boolean>;
  isLarge: () => Promise<boolean>;
  /** @deprecated use skin prop and isStandardSkin instead */
  isStandardTheme: () => Promise<boolean>;
  /** @deprecated use skin prop and isWarningSkin instead */
  isWarningTheme: () => Promise<boolean>;
  /** @deprecated use skin prop and isErrorSkin instead */
  isErrorTheme: () => Promise<boolean>;
  /** @deprecated use skin prop and isDarkSkin instead */
  isDarkTheme: () => Promise<boolean>;
  /** @deprecated use skin prop and isSuccessSkin instead */
  isSuccessTheme: () => Promise<boolean>;
  isStandardSkin: () => Promise<boolean>;
  isWarningSkin: () => Promise<boolean>;
  isErrorSkin: () => Promise<boolean>;
  isDarkSkin: () => Promise<boolean>;
  isSuccessSkin: () => Promise<boolean>;
  isRemovable: () => Promise<boolean>;
  removeTag: () => Promise<void>;
  isThumbExists: () => Promise<boolean>;
  isDisabled: () => Promise<boolean>;
  getLabel: () => Promise<string>;
  getCloseButtonSkin: () => Promise<string | null>;
  /** @deprecated use getCloseButtonSkin instead */
  getCloseButtonTheme: () => Promise<string | null>;
  getCloseButtonSize: () => Promise<string | null>;
  getTextSize: () => Promise<TextSize>;
  getTextWeight: () => Promise<TextWeight>;
  isClickable: () => Promise<boolean>;
  isHoverable: () => Promise<boolean>;
  pressKey: (key: RemoveKey) => Promise<void>;
}

export const tagUniDriverFactory = (base: UniDriver): TagUniDriver => {
  const removeButton = base.$(`[data-hook="${dataHooks.removeButton}"]`);
  const thumb = base.$(`[data-hook="${dataHooks.thumb}"]`);
  const textDriver = textUniDriverFactory(
    base.$(`[data-hook="${dataHooks.text}"]`),
  );

  return {
    ...baseUniDriverFactory(base),
    isTiny: async () => (await base.attr(dataAttr.SIZE)) === 'tiny',
    isSmall: async () => (await base.attr(dataAttr.SIZE)) === 'small',
    isMedium: async () => (await base.attr(dataAttr.SIZE)) === 'medium',
    isLarge: async () => (await base.attr(dataAttr.SIZE)) === 'large',
    isStandardTheme: async () =>
      (await base.attr(dataAttr.SKIN)) === 'standard',
    isWarningTheme: async () => (await base.attr(dataAttr.SKIN)) === 'warning',
    isErrorTheme: async () => (await base.attr(dataAttr.SKIN)) === 'error',
    isDarkTheme: async () => (await base.attr(dataAttr.SKIN)) === 'dark',
    isSuccessTheme: async () => (await base.attr(dataAttr.SKIN)) === 'success',
    isStandardSkin: async () => (await base.attr(dataAttr.SKIN)) === 'standard',
    isWarningSkin: async () => (await base.attr(dataAttr.SKIN)) === 'warning',
    isErrorSkin: async () => (await base.attr(dataAttr.SKIN)) === 'error',
    isDarkSkin: async () => (await base.attr(dataAttr.SKIN)) === 'dark',
    isSuccessSkin: async () => (await base.attr(dataAttr.SKIN)) === 'success',
    isRemovable: async () => removeButton.exists(),
    removeTag: async () => removeButton.click(),
    click: async () => base.click(),
    isThumbExists: () => thumb.exists(),
    isDisabled: async () => (await base.attr(dataAttr.DISABLED)) === 'true',
    getLabel: async () => base.text(),
    getCloseButtonSkin: async () => await removeButton.attr('data-skin'),
    getCloseButtonTheme: async () => await removeButton.attr('data-skin'),
    getCloseButtonSize: async () => await removeButton.attr('data-size'),
    getTextSize: async () => textDriver.getSize(),
    getTextWeight: async () => textDriver.getWeight(),
    isClickable: async () => (await base.attr(dataAttr.CLICKABLE)) === 'true',
    isHoverable: async () => (await base.attr(dataAttr.HOVERABLE)) === 'true',
    pressKey: async key => removeButton.pressKey(key),
  };
};
